/**
 * Service Price API Service
 * Handles all API calls related to service prices
 */

import { apiRequest } from "../../api/apiClient";
import type {
  ServiceCharge,
  CreateServiceChargeDto,
  UpdateServiceChargeDto,
} from "../../types/serviceCharge.types";

const handleResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(
      error.message ||
        error.title ||
        `HTTP ${response.status}: ${response.statusText}`,
    );
  }

  if (
    response.status === 204 ||
    response.headers.get("content-length") === "0"
  ) {
    return {} as T;
  }

  return response.json();
};

export const serviceChargeService = {
  /**
   * Get all service prices
   */
  getServiceCharges: async (): Promise<ServiceCharge[]> => {
    const response = await apiRequest("/ServiceCharges", {
      method: "GET",
    });
    // The ServiceCharge now uses 'Charge' instead of 'Price'
    return handleResponse<ServiceCharge[]>(response);
  },

  /**
   * Get a service charge by service category ID
   * Endpoint returns an array; this returns the first matching item.
   */
  getServiceChargeByCategoryId: async (
    serviceCategoryId: number,
  ): Promise<ServiceCharge> => {
    const response = await apiRequest(
      `/ServiceCharges/category/${serviceCategoryId}`,
      { method: "GET" },
    );
    const data = await handleResponse<ServiceCharge[]>(response);
    return data[0];
  },

  /**
   * Get a single service price by ID
   */
  getServiceChargeById: async (id: number): Promise<ServiceCharge> => {
    const response = await apiRequest(`/ServiceCharges/${id}`, {
      method: "GET",
    });
    // The ServiceCharge now uses 'Charge' instead of 'Price'
    return handleResponse<ServiceCharge>(response);
  },

  /**
   * Create a new service price
   */
  createServiceCharge: async (
    data: CreateServiceChargeDto,
  ): Promise<ServiceCharge> => {
    // Ensure 'Charge' is used instead of 'Price' in the payload
    const response = await apiRequest("/ServiceCharges", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
    return handleResponse<ServiceCharge>(response);
  },

  /**
   * Update an existing service price
   */
  updateServiceCharge: async (
    id: number,
    data: UpdateServiceChargeDto,
  ): Promise<ServiceCharge> => {
    // Ensure 'Charge' is used instead of 'Price' in the payload
    const response = await apiRequest(`/ServiceCharges/${id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
    return handleResponse<ServiceCharge>(response);
  },

  /**
   * Delete a service price
   */
  deleteServiceCharge: async (id: number): Promise<void> => {
    const response = await apiRequest(`/ServiceCharges/${id}`, {
      method: "DELETE",
    });
    await handleResponse<void>(response);
  },
};
